// Resumable GCS upload in chunks: after a failed chunk it queries the persisted offset and resumes.
// fake-gcs-server may answer that query with 200 instead of 308 (fsouza/fake-gcs-server#1362).

// GCS requires every non-final chunk to be a multiple of 256 KiB.
const CHUNK_SIZE = 8 * 1024 * 1024
const MAX_CHUNK_RETRIES = 3

type ChunkResult = {done: boolean; nextOffset: number}

/** `Range: bytes=0-N` means resume at N+1; no header means nothing persisted yet. */
function persistedOffset(rangeHeader: string | null): number {
  if (!rangeHeader) return 0
  const match = /bytes=0-(\d+)/.exec(rangeHeader)
  return match ? Number(match[1]) + 1 : 0
}

/** An empty PUT with `Content-Range: bytes *&sol;total`: 308 + `Range` to resume, 200/201 if complete. */
function queryOffset(sessionUrl: string, total: number): Promise<ChunkResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', sessionUrl)
    xhr.setRequestHeader('Content-Range', `bytes */${total}`)
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) return resolve({done: true, nextOffset: total})
      if (xhr.status === 308) return resolve({done: false, nextOffset: persistedOffset(xhr.getResponseHeader('Range'))})
      reject(new Error(`resume query failed (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('resume query failed'))
    xhr.send()
  })
}

function putChunk(
  sessionUrl: string,
  file: File,
  start: number,
  contentType: string,
  onProgress: (uploaded: number) => void
): Promise<ChunkResult> {
  const end = Math.min(start + CHUNK_SIZE, file.size)
  const total = file.size
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', sessionUrl)
    xhr.setRequestHeader('Content-Type', contentType)
    // `bytes 0-0/0` is not legal: an empty file sends `bytes */0` and completes at once.
    xhr.setRequestHeader('Content-Range', total === 0 ? `bytes */0` : `bytes ${start}-${end - 1}/${total}`)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(start + e.loaded)
    }
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) return resolve({done: true, nextOffset: total})
      if (xhr.status === 308) return resolve({done: false, nextOffset: persistedOffset(xhr.getResponseHeader('Range'))})
      reject(new Error(`upload failed (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('upload failed'))
    xhr.send(total === 0 ? new Blob([]) : file.slice(start, end))
  })
}

export async function uploadResumable(
  sessionUrl: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void
): Promise<void> {
  const total = file.size
  const report = (uploaded: number) => onProgress(total === 0 ? 100 : Math.round((uploaded / total) * 100))
  let offset = 0
  while (true) {
    let attempt = 0
    while (true) {
      try {
        const result = await putChunk(sessionUrl, file, offset, contentType, report)
        if (result.done) return report(total)
        offset = result.nextOffset
        report(offset)
        break
      } catch (err) {
        attempt += 1
        if (attempt >= MAX_CHUNK_RETRIES) throw err
        // The chunk may have landed despite the error: resume from the true offset.
        const status = await queryOffset(sessionUrl, total).catch(() => null)
        if (status?.done) return report(total)
        if (status) offset = status.nextOffset
        report(offset)
      }
    }
  }
}
